import { OrgHat } from '../interfaces/fe-models/organization-models';

export interface HatUserEntry {
    key: string;
    isPending: boolean;
}

/**
 * Returns all users on a hat (active + pending) with status flag.
 * Active users come first, then pending users.
 * Duplicates (user in both arrays) are returned as active only.
 */
export function getHatUsersWithStatus(hat: OrgHat): HatUserEntry[] {
    const active = hat.assignedToArray || hat.userKeys || [];
    const pending = hat.pendingAssignedToArray || [];
    const activeSet = new Set(active);

    return [
        ...active.map((key: string) => ({ key, isPending: false })),
        ...pending
            .filter((key: string) => !activeSet.has(key))
            .map((key: string) => ({ key, isPending: true })),
    ];
}

/**
 * Returns only active (approved) users on a hat.
 * Backward-compatible drop-in replacement for `hat.assignedToArray || hat.userKeys || []`.
 */
export function getActiveHatUsers(hat: OrgHat): string[] {
    return hat.assignedToArray || hat.userKeys || [];
}

/**
 * Returns only pending (unapproved) users on a hat.
 */
export function getPendingHatUsers(hat: OrgHat): string[] {
    return hat.pendingAssignedToArray || [];
}

/**
 * Returns the count of active users on a hat.
 */
export function getActiveHatUserCount(hat: OrgHat): number {
    return getActiveHatUsers(hat).length;
}

/**
 * Returns the count of pending users on a hat.
 */
export function getPendingHatUserCount(hat: OrgHat): number {
    return getPendingHatUsers(hat).length;
}

/**
 * Checks if a user is pending on the given hat.
 */
export function isUserPendingOnHat(hat: OrgHat, uid: string): boolean {
    return (hat.pendingAssignedToArray || []).includes(uid);
}

/**
 * Checks if a user is active (approved) on the given hat.
 */
export function isUserActiveOnHat(hat: OrgHat, uid: string): boolean {
    return (hat.assignedToArray || hat.userKeys || []).includes(uid);
}
